import { Injectable } from '@angular/core';
import { UserProfile, AuthUser, UserInfo } from '../interfaces/user.model';
import { AngularFireAuth } from '@angular/fire/auth';
import { Plugins } from '@capacitor/core';
const { Storage, Device } = Plugins;

import { FirebaseService } from './firebase.service';
import { environment } from 'src/environments/environment';
import { GTMService } from './gtm.service';
import { auth } from 'firebase';

const currentUserKey = 'current-user';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUser: UserInfo;
  private currentProfile: UserProfile;
  private creds: auth.UserCredential;
  private provider: auth.OAuthProvider | auth.GoogleAuthProvider
  interests: any;

  constructor(
    private firebaseSvc: FirebaseService,
    private gtmService: GTMService,
    private afAuth: AngularFireAuth) {
  }

  // ################ temporary for sapient only email verification #####################
  anonymousLogin() {
    return this.afAuth.signInAnonymously()
      .catch(err => {
        console.log('Auth Error: ', err);
        this.gtmService.analyticsEvent('errors', 'errors', 'error', 1, err);

      });
  }

  // ------------------------------------
  /** Registers a new user and set's its profile */
  async registerUser(user: AuthUser) {
    try {
      this.creds = await this.afAuth.createUserWithEmailAndPassword(user.email, user.password);
      user.id = this.creds.user.uid;

      await this.setCurrentUser(user);
      this.setUserProfile(user);

      return null;

    } catch (err) {
      console.log('Register error: ' + err);
      this.gtmService.analyticsEvent('errors', 'errors', 'error', 1, err);

      return err;
    }
  }

  // ------------------------------------
  /** log in the user */
  async loginUser(user: AuthUser, type?: string) {
    try {
      switch (type) {
        case 'google':
          this.provider = new auth.GoogleAuthProvider();
          console.log(this.provider)
          await this.afAuth.signInWithRedirect(this.provider);
          break;
        case 'apple':
          this.provider = new auth.OAuthProvider('apple.com');
          await this.afAuth.signInWithRedirect(this.provider);
          break;
        default:
          this.creds = await this.afAuth.signInWithEmailAndPassword(user.email, user.password);
          user.id = this.creds.user.uid;
          await this.setCurrentUser(user, true);
      }
      return null;

    } catch (err) {
      console.error('Login error: ' + err.message);
      this.gtmService.analyticsEvent('errors', 'errors', 'error', 1, err);

      return err.message;
    }
  }

  // ------------------------------------
  /** Logs out the current user */
  async logoutUser() {
    const storedUser: UserInfo = await this.getStoredUser();
    if (storedUser) {
      storedUser.loggedIn = false;
      await this.setStoredUser(storedUser);
    }

    return this.afAuth.signOut();
  }

  // ------------------------------------
  /** Sends reset pw email */
  async resetPassword(email: string): Promise<void> {
    return this.afAuth.sendPasswordResetEmail(email);
  }

  // ------------------------------------
  /** Returns the status of current user's logged in */
  async isLoggedIn() {
    const user = await this.getUser();
    return user?.loggedIn;
  }

  // ------------------------------------
  /** gets the current user */
  async getUser() {
    if (!this.currentUser) {
      const storedUser = await this.getStoredUser();
      if (storedUser) {
        this.currentUser = storedUser;
      }
    }

    if (this.currentUser?.loggedIn) {
      if (!this.currentProfile) {
        this.currentProfile = await this.firebaseSvc.getUserProfile(this.currentUser.email).toPromise()
      }
    }

    return this.currentUser;
  }

  async setUserBadges(badge: 'spotItem' | 'spotStore' | 'barcodeScanner' | 'shoppingLister') {
    this.currentProfile.badges[badge] = true;
    await this.firebaseSvc.saveUserProfile(this.currentProfile);
  }

  async setInterests(interests: any) {
    this.currentProfile.interests = interests;
    await this.firebaseSvc.saveUserProfile(this.currentProfile);
  }

  async getInterests() {
    this.getUser();
    return await this.currentProfile.interests;
  }

  // ===========================================
  async setCurrentUser(user: AuthUser, login?: boolean) {
    const info: UserInfo = {
      id: user.id,
      email: user.email,
      remember: user.remember,
      loggedIn: true
    };

    this.currentUser = info;

    await Storage.set({ key: currentUserKey, value: JSON.stringify(info) });

    if (login) {
      this.currentProfile = await this.firebaseSvc.getUserProfile(user.email).toPromise();
    }
  }

  // ======================================
  private async setUserProfile(user: AuthUser) {
    const device = await Device.getInfo();
    this.currentProfile = {
      id: user.id,
      name: user.name,
      email: user.email,
      emailNotification: user.emailNotification,
      appVersion: environment.app.version,
      lastUpdate: (new Date()).toISOString(),
      device: `${device.platform},${device.uuid}`,
      badges: {
        spotItem: false,
        spotStore: false,
        barcodeScanner: false,
        shoppingLister: false
      },
      interests: [],
      location: {
        latitude: null,
        longitude: null,
        zipcode: null
      }

    };
    await this.firebaseSvc.saveUserProfile(this.currentProfile);
  }

  // ======================================
  private async getStoredUser() {
    const user = await Storage.get({ key: currentUserKey });
    return user ? JSON.parse(user.value) : null;
  }

  // ======================================
  private async setStoredUser(user: UserInfo) {
    await Storage.set({ key: currentUserKey, value: JSON.stringify(user) });
  }
}
